import { useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import styled from '@emotion/styled';
import { Typography } from '@kizen/kds/Typography';
import BasicModal from '__components/Modals/presets/BasicModal';
import LongText from '__components/Inputs/LongText';
import SectionRaw from 'components/Wizards/shared/components/section';
import {
  parseCustomCodeBlock,
  serializeCustomCodeBlock,
  type CustomCodeContent,
  type CustomCodeParseError,
} from 'components/DashboardGrid/hooks/customCodeBlock';
import type { CustomCodeEditorResult } from 'components/DashboardGrid/hooks/useCustomCodeBlock';
import { shouldShowCodeError } from './editorState';

type CustomCodeBlockEditorProps = {
  initialContent: CustomCodeContent;
  onHide: () => void;
  onConfirm: (result: CustomCodeEditorResult) => void;
};

const Section = SectionRaw as ComponentType<{
  active: boolean;
  done: boolean;
  index: number;
  title: string;
  last?: boolean;
  children: ReactNode;
}>;

const CODE_INPUT_HEIGHT = 320;

const Intro = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 15px 20px;
  margin-bottom: 20px;
`;

const IntroText = styled.div`
  flex: 1 1 300px;
  min-width: 0;
`;

const getParseErrorMessage = (error: CustomCodeParseError, t: TFunction) => {
  switch (error) {
    case 'empty':
      return t('Paste the block code to continue.');
    case 'invalid_json':
      return t('The block code must be valid JSON.');
    case 'not_an_object':
      return t('The block code must be a JSON object.');
    case 'missing_script':
      return t('The block code must include a "script".');
    case 'invalid_name':
      return t('The block "name" must be text.');
    case 'invalid_styles':
      return t('The block "styles" must be text.');
    case 'invalid_event_scripts':
      return t(
        'The block "event_scripts" must map each event script name to its code.'
      );
    case 'invalid_dimensions':
      return t(
        'The block "min_w", "max_w", "min_h", and "max_h" must be whole numbers greater than zero, with "min_w" no greater than "max_w" and "min_h" no greater than "max_h".'
      );
    case 'invalid_default_dimensions':
      return t(
        'The block "default_w" and "default_h" must be whole numbers greater than zero, within the block\'s "min_w"/"max_w" and "min_h"/"max_h".'
      );
    case 'invalid_host_chrome':
      return t('The block "host_chrome" must be true or false.');
    case 'invalid_views':
      return t(
        'The block "views" must be a list of views, each with an "api_name", a "type" of "script" or "html", and an "event_scripts" list.'
      );
    case 'unsupported_content':
      return t(
        'The block code contains unsupported content (a null character or "{__ref:").'
      );
  }
};

export const CustomCodeBlockEditor = ({
  initialContent,
  onHide,
  onConfirm,
}: CustomCodeBlockEditorProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState(() =>
    serializeCustomCodeBlock(initialContent)
  );
  const [touched, setTouched] = useState(false);

  const parsed = useMemo(() => parseCustomCodeBlock(code), [code]);
  const showError = shouldShowCodeError(parsed, touched);

  const handleConfirm = () => {
    if (parsed.ok) {
      onConfirm({ content: parsed.content });
    }
  };

  return (
    <BasicModal
      heading={t('Edit Custom Block')}
      buttonText={t('Save')}
      defaultLeftBtnText={t('Cancel')}
      actionBtnColor="green"
      disabled={!parsed.ok}
      onConfirm={handleConfirm}
      onHide={onHide}
      show
      size="large"
      typeOfContent="new-wizard"
      enforceFocus={false}
      autoFocus={false}
      restoreFocus={false}
    >
      <Section
        active
        done={parsed.ok}
        index={0}
        title={t('Paste Block Code')}
        last
      >
        <Intro>
          <IntroText>
            <Typography>
              {t(
                'Edit the block JSON below, or ask Claude to update the block; it can push updates to this dashlet directly.'
              )}
            </Typography>
          </IntroText>
        </Intro>
        <LongText
          value={code}
          onChange={(text: string) => {
            setCode(text);
            setTouched(true);
          }}
          placeholder={t('Paste the block JSON generated by Claude')}
          initialHeight={CODE_INPUT_HEIGHT}
          error={showError}
          data-qa-id="custom-code-block-editor-code"
        />
        {showError && !parsed.ok ? (
          <Typography
            className="mt-[10px]"
            size="sm"
            color="feedback-error"
            qa={{ id: 'custom-code-block-editor-error' }}
          >
            {getParseErrorMessage(parsed.error, t)}
          </Typography>
        ) : null}
      </Section>
    </BasicModal>
  );
};
